(
    function () {
        async function fetchVariantMetafield(variantGid, namespace, key) {
            const endpoint = 'https://hola-bb.myshopify.com/api/2024-04/graphql.json';
            const token = 'not-yet';
            
            const query = `
                query {
                productVariant(id: "${variantGid}") {
                    metafield(namespace: "${namespace}", key: "${key}") {
                    value
                    }
                }
                }
            `;        
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': token
                },
                body: JSON.stringify({ query })
            });
            
            const result = await response.json();
            return result.data.productVariant?.metafield?.value;
        }
        
        const DEBUG_TIMER = false;
        const TESTING_SYMBOL = Symbol("shipping-timer-testing");
        const testRegistry = new WeakMap();
        
        const debugLog = (location, message, data = null) => {
            if (!DEBUG_TIMER) return;
            const timestamp = new Date().toISOString();
            console.log(`[SHIPPING-TIMER ${timestamp}] ${location}:`, message, data !== null ? data : '');
        };
        
        debugLog('Init', '=== Shipping Timer Script Loaded ===', { DEBUG_TIMER });
        
        class ShippingTimerCore {
            constructor(warehouseService, startDate = new Date()) {
                debugLog('ShippingTimerCore.constructor', 'Initializing', { startDate });
                this.warehouseService = warehouseService;
                this.startDate = startDate;
            }
            
            getTimerDisplay(messages, pageLocale, elements, region) {
                const currentDate = new Date();
                debugLog('ShippingTimerCore.getTimerDisplay', 'Getting timer display', { currentDate });
                const timeLeft = this.warehouseService.getTimeLeftToday(currentDate);
                debugLog('ShippingTimerCore.getTimerDisplay', 'Time left calculated', { timeLeft });

                if (timeLeft && timeLeft > 0) {
                    debugLog('ShippingTimerCore.getTimerDisplay', 'Creating countdown display');
                    const formatter = new CountdownFormatter(timeLeft, messages);
                    const renderer = new CountdownRenderer(elements, timeLeft, region, messages);
                    return new CountdownDisplay(formatter, renderer);
                } else {
                    debugLog('ShippingTimerCore.getTimerDisplay', 'Creating next day display');
                    const nextDate = this.warehouseService.getNextShippingDay(currentDate);
                    debugLog('ShippingTimerCore.getTimerDisplay', 'Next shipping day', { nextDate });
                    const formatter = new NextDayFormatter(nextDate, currentDate, messages, pageLocale, this.warehouseService.warehouseTimeZone);
                    const renderer = new NextDayRenderer(elements, region, messages);
                    return new NextDayDisplay(formatter, renderer);
                }
            }
        }
        
        class TimerDisplay {
            constructor(formatter, renderer) {
                debugLog('TimerDisplay.constructor', 'Initializing');
                this.formatter = formatter;
                this.renderer = renderer;
            }
            
            render() {
                try {
                    debugLog('TimerDisplay.render', 'Rendering display');
                    const displayData = this.formatter.format();
                    debugLog('TimerDisplay.render', 'Display data formatted', displayData);
                    this.renderer.render(displayData);
                    debugLog('TimerDisplay.render', 'Display rendered');
                } catch (error) {
                    debugLog('TimerDisplay.render', 'ERROR rendering display', { 
                        error: error.message, 
                        stack: error.stack 
                    });
                    throw error;
                }
            }
        }
        class CountdownDisplay extends TimerDisplay {}
        class NextDayDisplay extends TimerDisplay {}
        
        
        class CountdownFormatter {
            constructor(timeLeft, messages) {
                debugLog('CountdownFormatter.constructor', 'Initializing', { timeLeft, messages });
                this.timeLeft = timeLeft;
                this.messages = messages;
            }
            
            format() {
                debugLog('CountdownFormatter.format', 'Formatting countdown');
                const { hours, minutes, seconds } = this._calculateTimeUnits();
                debugLog('CountdownFormatter.format', 'Time units calculated', { hours, minutes, seconds });
                
                return {
                    prefix: this.messages.prefix,
                    countdown: this.messages.countdown
                    .replace("{hours}", hours)
                    .replace("{minutes}", minutes)
                    .replace("{seconds}", seconds),
                    message: this.messages.timer
                };
            }
            
            _calculateTimeUnits() {
                const hours = Math.floor(this.timeLeft / (1000 * 60 * 60));
                const minutes = Math.floor((this.timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((this.timeLeft % (1000 * 60)) / 1000);
                
                return { hours, minutes, seconds };
            }
        }
        
        class NextDayFormatter {
            constructor(nextDate, startDate, messages, pageLocale, warehouseTimeZone) {
                debugLog('NextDayFormatter.constructor', 'Initializing', { nextDate, startDate, pageLocale, warehouseTimeZone });
                this.nextDate = nextDate;
                this.startDate = startDate;
                this.messages = messages;
                this.pageLocale = pageLocale;
                this.warehouseTimeZone = warehouseTimeZone;
            }
            
            format() {
                debugLog('NextDayFormatter.format', 'Formatting next day');
                const dateText = this._formatNextShippingDate();
                debugLog('NextDayFormatter.format', 'Date text formatted', { dateText });
                
                return {
                    prefix: "",
                    countdown: "",
                    message: this.messages.nextDay.replace("{date}", dateText)
                };
            }
            
            _formatNextShippingDate() {
                const daysAhead = Math.ceil((this.nextDate - this.startDate) / (1000 * 60 * 60 * 24));
                debugLog('NextDayFormatter._formatNextShippingDate', 'Days ahead calculated', { daysAhead });
                
                return daysAhead === 1 ? 
                this.messages.tomorrow :
                this.nextDate.toLocaleDateString(this.pageLocale, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    timeZone: this.warehouseTimeZone
                });
            }
        }
        
        class CountdownRenderer {
            constructor(elements, timeLeft, region, messages) {
                debugLog('CountdownRenderer.constructor', 'Initializing', { timeLeft, isUrgent: timeLeft < (1000 * 60 * 60) });
                this.elements = elements;
                this.isUrgent = timeLeft < (1000 * 60 * 60); // Less than 1 hour
                this.region = region;
                this.messages = messages;
            }

            render(displayData) {
                debugLog('CountdownRenderer.render', 'Rendering countdown', displayData);
                this.elements.prefix.textContent = displayData.prefix;
                this.elements.countdown.textContent = displayData.countdown;
                this.elements.message.textContent = displayData.message;
                this.elements.suffix.textContent = this.region === "uk" ? this.messages.excludingNi : "";
                this.elements.container.classList.toggle("shipping-timer--urgent", this.isUrgent);
                this.elements.container.classList.remove("shipping-timer--closed");
                debugLog('CountdownRenderer.render', 'Countdown rendered successfully');
            }
        }
        
        class NextDayRenderer {
            constructor(elements, region, messages) {
                debugLog('NextDayRenderer.constructor', 'Initializing');
                this.elements = elements;
                this.region = region;
                this.messages = messages;
            }

            render(displayData) {
                debugLog('NextDayRenderer.render', 'Rendering next day', displayData);
                this.elements.prefix.textContent = displayData.prefix;
                this.elements.countdown.textContent = displayData.countdown;
                this.elements.message.textContent = displayData.message;
                this.elements.suffix.textContent = this.region === "uk" ? this.messages.excludingNi : "";
                this.elements.container.classList.add("shipping-timer--closed");
                debugLog('NextDayRenderer.render', 'Next day rendered successfully');
            }
        }
        
        class Throttler {
            constructor(interval) {
                this.interval = interval;
                this.lastUpdate = 0;
            }
            
            shouldUpdate() {
                const now = Date.now();
                if (now - this.lastUpdate >= this.interval) {
                    this.lastUpdate = now;
                    return true;
                }
                return false;
            }
        }
        
        class ShippingTimerController {
            constructor(core, messages, pageLocale, elements, throttler, region) {
                debugLog('ShippingTimerController.constructor', 'Initializing', { throttlerInterval: throttler.interval });
                this.core = core;
                this.messages = messages;
                this.pageLocale = pageLocale;
                this.elements = elements;
                this.throttler = throttler;
                this.region = region;
                this.interval = null;
            }
            
            start() {
                debugLog('ShippingTimerController.start', 'Starting timer controller');
                this.updateTimer();
                this.interval = setInterval(() => this.throttledUpdate(), this.throttler.interval);
                debugLog('ShippingTimerController.start', 'Timer started with interval', { intervalId: this.interval });
                return this;
            }
            
            throttledUpdate() {
                if (this.throttler.shouldUpdate()) {
                    debugLog('ShippingTimerController.throttledUpdate', 'Throttle passed, updating timer');
                    this.updateTimer();
                }
            }
            
            updateTimer() {
                debugLog('ShippingTimerController.updateTimer', 'Updating timer');
                const display = this.core.getTimerDisplay(this.messages, this.pageLocale, this.elements, this.region);
                display.render();
            }
            
            destroy() {
                debugLog('ShippingTimerController.destroy', 'Destroying timer controller', { intervalId: this.interval });
                if (this.interval) {
                    clearInterval(this.interval);
                    this.interval = null;
                }
            }
        }
        
        class ShippingTimer {
            static create(container) {
                try {
                    debugLog('ShippingTimer.create', 'Starting creation', { hasContainer: !!container });
                    if (!container) {
                        debugLog('ShippingTimer.create', 'No container found, returning null');
                        return null;
                    }
                    
                    // Parse container data
                    const messages = {
                        prefix: container.dataset.timerPrefix,
                        countdown: container.dataset.timerCountdown,
                        timer: container.dataset.timerMessage,
                        nextDay: container.dataset.nextDayMessage,
                        tomorrow: container.dataset.tomorrowMessage,
                        excludingNi: container.dataset.excludingNiMessage,
                    };
                    debugLog('ShippingTimer.create', 'Messages parsed', messages);
                    
                    const pageLocale = container.dataset.shippingLocaleIso;
                    debugLog('ShippingTimer.create', 'Page locale', { pageLocale });
                    
                    // Create elements object
                    const elements = {
                        container,
                        prefix: container.querySelector(".shipping-timer__prefix"),
                        countdown: container.querySelector(".shipping-timer__countdown"),
                        message: container.querySelector(".shipping-timer__message"),
                        suffix: container.querySelector(".shipping-timer__suffix"),
                    };
                    debugLog('ShippingTimer.create', 'Elements found', {
                        hasPrefix: !!elements.prefix,
                        hasCountdown: !!elements.countdown,
                        hasMessage: !!elements.message
                    });
                    
                    // Create dependencies
                    const cutoffHour = parseInt(container.dataset.cutoffHour, 10);
                    const cutoffMinute = parseInt(container.dataset.cutoffMinute, 10);
                    const region = container.dataset.warehouseRegion || "nl";
                    debugLog('ShippingTimer.create', 'Cutoff time', { cutoffHour, cutoffMinute, region });

                    const timeZone = region === "uk" ? "Europe/London" : "Europe/Amsterdam";
                    const holidays = region === "uk" ? UK_WAREHOUSE_HOLIDAYS : NL_WAREHOUSE_HOLIDAYS;
                    const warehouseService = new WarehouseService(cutoffHour, cutoffMinute, timeZone, holidays);
                    
                    const core = new ShippingTimerCore(warehouseService);
                    
                    const isMobile = window.innerWidth <= 768 || "ontouchstart" in window;
                    debugLog('ShippingTimer.create', 'Device detection', { isMobile, windowWidth: window.innerWidth });
                    const throttler = new Throttler(isMobile ? 1000 : 1000);
                    
                    // Create and start controller
                    debugLog('ShippingTimer.create', 'Creating controller');
                    return new ShippingTimerController(core, messages, pageLocale, elements, throttler, region).start();
                } catch (error) {
                    debugLog('ShippingTimer.create', 'ERROR creating timer', { 
                        error: error.message, 
                        stack: error.stack 
                    });
                    throw error;
                }
            }
            
            constructor(container) {
                return ShippingTimer.create(container);
            }
        }
        
        const NL_WAREHOUSE_HOLIDAYS = [
            "2025-10-06", // Warehouse closed
            "2025-12-25", // Christmas Day
            "2025-12-26", // Second Christmas Day
            "2025-12-31", // New Year's Eve
            "2026-01-01", // New Year's Day
            "2026-04-03", // Good Friday
            "2026-04-05", // Easter Sunday
            "2026-04-06", // Easter Monday
            "2026-04-27", // King's Day
            "2026-05-05", // Liberation Day
            "2026-05-14", // Ascension Day
            "2026-05-24", // Whit Sunday
            "2026-05-25", // Whit Monday
            "2026-12-25", // Christmas Day
            "2026-12-26", // Boxing Day
        ];

        const UK_WAREHOUSE_HOLIDAYS = [
            "2025-12-25", // Christmas Day
            "2025-12-26", // Boxing Day
            "2026-01-01", // New Year's Day
            "2026-04-03", // Good Friday
            "2026-04-06", // Easter Monday
            "2026-05-04", // Early May bank holiday
            "2026-05-25", // Spring bank holiday
            "2026-08-31", // Summer bank holiday
            "2026-12-25", // Christmas Day
            "2026-12-28", // Boxing Day (substitute)
        ];

        class WarehouseService {
            constructor(cutoffHour, cutoffMinute, timeZone = "Europe/Amsterdam", holidays = NL_WAREHOUSE_HOLIDAYS) {
                debugLog('WarehouseService.constructor', 'Initializing', { cutoffHour, cutoffMinute, timeZone });
                this.cutoffHour = cutoffHour;
                this.cutoffMinute = cutoffMinute;
                this.timeZone = timeZone;
                this.holidays = holidays;
                
                this.warehouseTimezoneIsoOffset = this.getWarehouseTimezoneIsoOffset();
                this.cutoffTimeIso = this.formatCutoffTime();
                debugLog('WarehouseService.constructor', 'Configuration', {
                    timeZone: this.timeZone,
                    warehouseTimezoneIsoOffset: this.warehouseTimezoneIsoOffset,
                    cutoffTimeIso: this.cutoffTimeIso
                });
                
                this.warehouseTimezoneFormatter = new Intl.DateTimeFormat("en-GB", {
                    timeZone: this.timeZone,
                    weekday: "narrow",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                });
            }
            
            get warehouseTimeZone() {
                return this.timeZone;
            }
            
            getWarehouseTimezoneIsoOffset() {
                const offsetString = new Date().toLocaleString("en-GB", {
                    timeZone: this.timeZone,
                    timeZoneName: "shortOffset",
                });
                const match = offsetString.match(/GMT([+-])(\d+)/);
                if (!match) return "+00:00";
                const [_, sign, hours] = match;
                return `${sign}${hours.padStart(2, "0")}:00`;
            }
            
            formatCutoffTime() {
                return `T${String(this.cutoffHour).padStart(2, "0")}:${String(this.cutoffMinute).padStart(2, "0")}:00${this.warehouseTimezoneIsoOffset}`;
            }
            
            isHoliday(dateParts) {
                const year = this.getDatePart(dateParts, "year");
                const month = this.getDatePart(dateParts, "month");
                const day = this.getDatePart(dateParts, "day");
                const dateString = `${year}-${month}-${day}`;
                const isHoliday = this.holidays.includes(dateString);
                debugLog('WarehouseService.isHoliday', 'Checking holiday', { dateString, isHoliday });
                return isHoliday;
            }
            
            isWorkingDay(dateParts) {
                const weekday = this.getDatePart(dateParts, "weekday");
                const isWeekend = weekday === "S";
                const isHoliday = this.isHoliday(dateParts);
                const isWorking = !isWeekend && !isHoliday;
                debugLog('WarehouseService.isWorkingDay', 'Checking working day', { weekday, isWeekend, isHoliday, isWorking });
                return isWorking;
            }
            
            getDatePart(parts, type) {
                return parts.find((p) => p.type === type)?.value;
            }
            
            calculateTimeLeft(warehouseTimeZoneDateParts) {
                const dateComponents = ["year", "month", "day"].map((type) => this.getDatePart(warehouseTimeZoneDateParts, type));
                const timeComponents = ["hour", "minute", "second"].map((type) => this.getDatePart(warehouseTimeZoneDateParts, type));
                
                const nowInWarehouseTimezone = new Date(`${dateComponents.join("-")}T${timeComponents.join(":")}${this.warehouseTimezoneIsoOffset}`);
                const cutoffTime = new Date(`${dateComponents.join("-")}${this.cutoffTimeIso}`);
                const timeLeft = cutoffTime - nowInWarehouseTimezone;
                
                debugLog('WarehouseService.calculateTimeLeft', 'Calculating time left', {
                    nowInWarehouseTimezone: nowInWarehouseTimezone.toISOString(),
                    cutoffTime: cutoffTime.toISOString(),
                    timeLeft,
                    timeLeftHours: Math.floor(timeLeft / (1000 * 60 * 60))
                });
                
                return timeLeft;
            }
            
            getTimeLeftToday(currentDate = new Date()) {
                debugLog('WarehouseService.getTimeLeftToday', 'Getting time left today', { currentDate });
                const warehouseTimezoneDateParts = this.warehouseTimezoneFormatter.formatToParts(currentDate);
                
                if (!this.isWorkingDay(warehouseTimezoneDateParts)) {
                    debugLog('WarehouseService.getTimeLeftToday', 'Not a working day, returning null');
                    return null;
                }
                const timeLeft = this.calculateTimeLeft(warehouseTimezoneDateParts);
                debugLog('WarehouseService.getTimeLeftToday', 'Time left calculated', { timeLeft });
                return timeLeft;
            }
            
            getNextShippingDay(currentDate = new Date()) {
                debugLog('WarehouseService.getNextShippingDay', 'Finding next shipping day', { currentDate });
                const nextDate = new Date(currentDate);
                nextDate.setDate(nextDate.getDate() + 1);
                
                let iterations = 0;
                while (!this.isWorkingDay(this.warehouseTimezoneFormatter.formatToParts(nextDate))) {
                    nextDate.setDate(nextDate.getDate() + 1);
                    iterations++;
                    if (iterations > 30) {
                        debugLog('WarehouseService.getNextShippingDay', 'WARNING: Too many iterations finding next shipping day', { iterations });
                        break;
                    }
                }
                
                debugLog('WarehouseService.getNextShippingDay', 'Next shipping day found', { nextDate, iterations });
                return nextDate;
            }
        }
        
        // Initialize and handle cleanup
        let shippingTimer = null;
        
        document.addEventListener("DOMContentLoaded", () => {
            debugLog('EventHandler', 'DOMContentLoaded fired');
            const container = document.querySelector("[data-shipping-timer]");
            debugLog('EventHandler', 'Container search result', { hasContainer: !!container });
            if (container) {
                debugLog('EventHandler', 'Creating new ShippingTimer from DOMContentLoaded');
                shippingTimer = new ShippingTimer(container);
            }
            
            // Handle variant changes
            document.addEventListener("variantChange", (event) => {
                debugLog('EventHandler', 'variantChange event received', { variant: event.detail?.variant });
                const variant = event.detail.variant;
                if (!variant) {
                    debugLog('EventHandler', 'No variant in event, ignoring');
                    return;
                }
                
                if (shippingTimer) {
                    debugLog('EventHandler', 'Destroying existing timer before variant change');
                    shippingTimer.destroy();
                    shippingTimer = null;
                }
                
                const container = document.querySelector("[data-shipping-timer]");
                const isEligible = variant.metafields?.custom?.available_at_warehouse;
                debugLog('EventHandler', 'Variant eligibility check', { isEligible, hasContainer: !!container });
                
                if (isEligible && container) {
                    debugLog('EventHandler', 'Variant eligible, showing and creating timer');
                    container.classList.remove("hidden");
                    shippingTimer = new ShippingTimer(container);
                } else if (container) {
                    debugLog('EventHandler', 'Variant not eligible, hiding timer');
                    container.classList.add("hidden");
                }
            });
            
            // Handle variant changes when the product uses BCPO app
            const observer = new MutationObserver(() => {
                const bcpoSelect = document.querySelector('select#bcpo-select');
                if (bcpoSelect) {
                    bcpoSelect.addEventListener('change', e => { 
                        if(window.bcpo?.variant){
                            debugLog('EventHandler', 'BCPO change event, variant is now:', window.bcpo?.variant)
                            if (shippingTimer) {
                                debugLog('EventHandler', 'Destroying existing timer before BCPO variant change');
                                shippingTimer.destroy();
                                shippingTimer = null;
                            }
                            
                            const container = document.querySelector("[data-shipping-timer]");
                            const variant = window.bcpo.variant;
                            if (variant && variant.id) {
                                const metafieldValue = null;
                                // Example usage:
                                const variantGid = "gid://shopify/ProductVariant/" + variant.id;
                                fetchVariantMetafield(variantGid, "custom", "available_at_warehouse")
                                .then(value => {
                                    const isEligible = value === "true" || value === true;
                                    debugLog('EventHandler', 'Variant eligibility check', { isEligible, hasContainer: !!container });
                                    
                                    if (isEligible && container) {
                                        debugLog('EventHandler', 'Variant eligible, showing and creating timer');
                                        container.classList.remove("hidden");
                                        shippingTimer = new ShippingTimer(container);
                                    } else if (container) {
                                        debugLog('EventHandler', 'Variant not eligible, hiding timer');
                                        container.classList.add("hidden");
                                    }
                                });
                            }
                        }
                    });
                    debugLog('MutationObserver', 'bcpoSelect found');
                    observer.disconnect(); // Stop observing once found
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
            
            // Handle Shopify section updates
            document.addEventListener("shopify:section:load", (event) => {
                debugLog('EventHandler', 'shopify:section:load event received');
                if (shippingTimer) {
                    debugLog('EventHandler', 'Destroying timer before section load');
                    shippingTimer.destroy();
                    shippingTimer = null;
                }
                
                const newContainer = event.target.querySelector("[data-shipping-timer]");
                debugLog('EventHandler', 'Section load container search', { hasContainer: !!newContainer });
                if (newContainer) {
                    debugLog('EventHandler', 'Creating timer from section load');
                    shippingTimer = new ShippingTimer(newContainer);
                }
            });
            
            document.addEventListener("shopify:section:unload", () => {
                debugLog('EventHandler', 'shopify:section:unload event received');
                if (shippingTimer) {
                    debugLog('EventHandler', 'Destroying timer on section unload');
                    shippingTimer.destroy();
                    shippingTimer = null;
                }
            });
            
            window.addEventListener("beforeunload", () => {
                debugLog('EventHandler', 'beforeunload event received');
                if (shippingTimer) {
                    debugLog('EventHandler', 'Destroying timer before unload');
                    shippingTimer.destroy();
                    shippingTimer = null;
                }
            });
        });
        
        // Expose Classes for Shopify
        if (typeof window !== "undefined") {
            window.ShippingTimer = ShippingTimer;
            window.WarehouseService = WarehouseService;
            window.ShippingTimerController = ShippingTimerController;
            window.ShippingTimerCore = ShippingTimerCore;
            window.CountdownFormatter = CountdownFormatter;
            window.NextDayFormatter = NextDayFormatter;
            window.CountdownRenderer = CountdownRenderer;
            window.NextDayRenderer = NextDayRenderer;
            window.Throttler = Throttler;
        }
        
        // Expose for Jest (Only in Node.js)
        if (typeof module !== "undefined" && module.exports) {
            const exports = { 
                ShippingTimer, ShippingTimerController, Throttler, 
                ShippingTimerCore, CountdownDisplay, NextDayDisplay,
                CountdownFormatter, NextDayFormatter, CountdownRenderer, 
                NextDayRenderer, WarehouseService
            };
            testRegistry.set(TESTING_SYMBOL, exports);
            module.exports = { testRegistry, TESTING_SYMBOL };
        }
    })();

